import { useState, useCallback, useRef } from "react";
import ColorThief from "colorthief";
import tinycolor from "tinycolor2";

const useColorAccent = () => {
  const [colors, setColors] = useState({
    dominantColor: null,
    accentColor: null,
    counterColor: null,
  });
  const hasExtractedColors = useRef(false);

  const getDominantColor = useCallback(
    (img) => {
      // Prevent multiple extractions for the same image
      // if (hasExtractedColors.current) {
      //   return colors;
      // }

      try {
        const colorThief = new ColorThief();
        const colorRes = colorThief.getColor(img);

        // Convert RGB array to hex color
        const dominantColor = tinycolor(`rgb(${colorRes.join(",")})`);

        // Generate accent color - slightly lighter version
        const accentColor = dominantColor.clone().lighten(15);

        // Generate counter color optimized for dark mode with black background
        // Since the app has a black background, we need counter colors that stand out
        let counterColor;

        // Get HSL values for better color manipulation
        const hsl = accentColor.toHsl();

        // For light/medium colors, use bright contrasting colors that stand out on black
        if (accentColor.getLuminance() > 0.25) {
          // Create a bright version of the accent color's complementary hue
          const oppositeHue = (hsl.h + 180) % 360;
          counterColor = tinycolor({
            h: oppositeHue,
            s: Math.min(100, hsl.s + 10), // Keep good saturation
            l: 85, // Bright but not pure white
          });

          // If the opposite color is too similar in brightness, use a bright neutral
          const brightnessDiff = Math.abs(
            counterColor.getLuminance() - accentColor.getLuminance()
          );
          if (brightnessDiff < 0.3) {
            counterColor = tinycolor("#ffffff"); // White for maximum visibility on black
          }
        }
        // For very dark colors, use bright colors that stand out on black
        else if (accentColor.getLuminance() < 0.15) {
          counterColor = tinycolor("#ffffff"); // White
        }
        // For medium-dark colors, use bright contrasting colors
        else {
          // Use the opposite side of the color wheel for better contrast
          const oppositeHue = (hsl.h + 180) % 360;

          // Create a bright contrasting color that stands out on black
          counterColor = tinycolor({
            h: oppositeHue,
            s: Math.min(100, hsl.s + 20), // Increase saturation slightly
            l: 80, // Bright for visibility on black
          });
        }

        const newColors = {
          dominantColor: dominantColor.toHexString(),
          accentColor: accentColor.toHexString(),
          counterColor: counterColor.toHexString(),
        };

        setColors(newColors);
        hasExtractedColors.current = true;

        return newColors;
      } catch (error) {
        console.error("Error extracting dominant color:", error);
        // Fallback colors optimized for dark mode
        const fallbackColors = {
          dominantColor: "#3e3e46",
          accentColor: "#5a5a66",
          counterColor: "#ffffff", // White text on dark accent for better visibility
        };
        setColors(fallbackColors);
        hasExtractedColors.current = true;
        return fallbackColors;
      }
    },
    [colors]
  );

  const attachImage = useCallback(
    (imgElement) => {
      if (!imgElement) return;

      // Check if image is already loaded
      if (imgElement.complete) {
        getDominantColor(imgElement);
      } else {
        // Wait for image to load
        const handleLoad = () => {
          getDominantColor(imgElement);
          imgElement.removeEventListener("load", handleLoad);
        };
        imgElement.addEventListener("load", handleLoad);
      }
    },
    [getDominantColor]
  );

  return {
    accentColor: colors.accentColor,
    counterColor: colors.counterColor,
    attachImage,
  };
};

export default useColorAccent;
